import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, ExternalLink, Package, Calendar, DollarSign, 
  Truck, Building2, ClipboardCheck, PackageCheck 
} from "lucide-react";
import { toast } from 'sonner';

import { API } from '@/config';

export default function PurchaseOrderDetailPage() {
  const { poId } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deliveryAppointment, setDeliveryAppointment] = useState(null);

  useEffect(() => {
    fetchPO();
  }, [poId]);

  useEffect(() => {
    if (po) {
      fetchDeliveryAppointment();
      // Poll for status updates every 30 seconds
      const interval = setInterval(fetchDeliveryAppointment, 30000);
      return () => clearInterval(interval);
    }
  }, [po]);

  const fetchPO = async () => {
    try {
      const response = await axios.get(`${API}/warehouse/purchase-orders/${poId}`);
      setPo(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching PO:', error);
      toast.error('Failed to load purchase order');
      setLoading(false);
    }
  };

  const fetchDeliveryAppointment = async () => {
    try {
      // Fetch trucks and find one linked to this PO
      const response = await axios.get(`${API}/api/warehouse/trucks`);
      const appointment = response.data.find(truck => truck.po_number === po.po_number);
      setDeliveryAppointment(appointment);
    } catch (error) {
      console.error('Error fetching delivery appointment:', error);
    }
  };

  const openNetSuite = () => {
    if (po?.netsuite_id) {
      // In production, this would be the actual NetSuite URL
      window.open(`https://system.netsuite.com/app/accounting/transactions/purchord.nl?id=${po.netsuite_id}`, '_blank');
      toast.success('Opening NetSuite record...');
    }
  };

  const handleScheduleDelivery = () => {
    navigate('/warehouse/trucks', { state: { poNumber: po.po_number } });
  };

  const handleStartReceiving = () => {
    // Create a receipt and navigate to mobile receiving
    toast.success('Starting receipt process...');
    // This would create a receipt record first, then navigate
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      partially_received: 'bg-yellow-100 text-yellow-700',
      fully_received: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return (
      <Badge className={`${styles[status] || 'bg-gray-100 text-gray-700'} text-sm`}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">Purchase order not found</p>
          <Button onClick={() => navigate('/purchase-orders')} className="mt-4">
            Back to Purchase Orders
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/purchase-orders')}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{po.po_number}</h1>
              {getStatusBadge(po.status)}
            </div>
            <p className="text-gray-600">Purchase Order Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openNetSuite} className="gap-2">
            <ExternalLink className="w-4 h-4" />
            Open in NetSuite
          </Button>
          {po.status === 'open' && (
            <>
              <Button variant="outline" onClick={handleScheduleDelivery} className="gap-2">
                <Truck className="w-4 h-4" />
                Schedule Delivery
              </Button>
              <Button onClick={handleStartReceiving} className="gap-2 bg-green-600 hover:bg-green-700">
                <PackageCheck className="w-4 h-4" />
                Start Receiving
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="col-span-2 space-y-6">
          {/* PO Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5" />
                Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm text-gray-500">Vendor</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <p className="font-medium text-gray-900">{po.vendor}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Vendor ID</label>
                  <p className="font-medium text-gray-900 mt-1">{po.vendor_id || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Order Date</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <p className="font-medium text-gray-900">{po.order_date}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Expected Delivery</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    <p className="font-medium text-gray-900">{po.expected_delivery_date}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Total Amount</label>
                  <div className="flex items-center gap-2 mt-1">
                    <DollarSign className="w-4 h-4 text-green-400" />
                    <p className="font-bold text-gray-900 text-lg">
                      ${po.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-500">NetSuite ID</label>
                  <p className="font-medium text-gray-900 mt-1">{po.netsuite_id}</p>
                </div>
              </div>
              {po.shipping_address && (
                <div>
                  <label className="text-sm text-gray-500">Shipping Address</label>
                  <p className="font-medium text-gray-900 mt-1 whitespace-pre-line">{po.shipping_address}</p>
                </div>
              )}
              {po.notes && (
                <div>
                  <label className="text-sm text-gray-500">Notes</label>
                  <p className="text-gray-900 mt-1">{po.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Line Items ({po.line_items.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Line</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ordered</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Received</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {po.line_items.map((item) => (
                      <tr key={item.line_number} className="hover:bg-gray-50">
                        <td className="px-3 py-3 text-sm text-gray-900">{item.line_number}</td>
                        <td className="px-3 py-3 text-sm text-gray-900">{item.item_name}</td>
                        <td className="px-3 py-3 text-sm text-gray-600">{item.sku}</td>
                        <td className="px-3 py-3 text-sm text-right text-gray-900">
                          {item.quantity_ordered} {item.unit}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-green-600 font-medium">
                          {item.quantity_received} {item.unit}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-blue-600 font-medium">
                          {item.quantity_remaining} {item.unit}
                        </td>
                        <td className="px-3 py-3 text-sm text-right text-gray-900">
                          ${item.unit_price.toFixed(2)}
                        </td>
                        <td className="px-3 py-3 text-sm text-right font-medium text-gray-900">
                          ${item.total_price.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Summary & Actions */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-sm text-gray-500">Total Items</div>
                <div className="text-2xl font-bold text-gray-900">{po.line_items.length}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total Ordered</div>
                <div className="text-2xl font-bold text-gray-900">
                  {po.line_items.reduce((sum, item) => sum + item.quantity_ordered, 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Total Received</div>
                <div className="text-2xl font-bold text-green-600">
                  {po.line_items.reduce((sum, item) => sum + item.quantity_received, 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Completion</div>
                <div className="text-2xl font-bold text-blue-600">
                  {(() => {
                    const totalOrdered = po.line_items.reduce((sum, item) => sum + item.quantity_ordered, 0);
                    const totalReceived = po.line_items.reduce((sum, item) => sum + item.quantity_received, 0);
                    return totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;
                  })()}%
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Appointment */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Delivery Appointment
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deliveryAppointment ? (
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status</span>
                    <Badge className={
                      deliveryAppointment.status === 'checked_in' ? 'bg-blue-100 text-blue-700' :
                      deliveryAppointment.status === 'unloading' ? 'bg-orange-100 text-orange-700' :
                      deliveryAppointment.status === 'completed' ? 'bg-green-100 text-green-700' :
                      deliveryAppointment.status === 'scheduled' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }>
                      {deliveryAppointment.status === 'unloading' ? '🚚 Being Unloaded' :
                       deliveryAppointment.status === 'checked_in' ? '✓ Checked In' :
                       deliveryAppointment.status === 'completed' ? '✓ Completed' :
                       deliveryAppointment.status === 'scheduled' ? '📅 Scheduled' :
                       deliveryAppointment.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>

                  {/* Appointment Details */}
                  <div className="space-y-3 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Date & Time</span>
                      <span className="text-sm font-medium">
                        {new Date(deliveryAppointment.date).toLocaleDateString()} at {deliveryAppointment.time_slot}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Confirmation #</span>
                      <span className="text-sm font-mono font-medium">{deliveryAppointment.confirmation_number}</span>
                    </div>

                    {deliveryAppointment.carrier && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Carrier</span>
                        <span className="text-sm font-medium">{deliveryAppointment.carrier}</span>
                      </div>
                    )}

                    {deliveryAppointment.dock_door && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Dock Door</span>
                        <span className="text-sm font-medium">{deliveryAppointment.dock_door}</span>
                      </div>
                    )}
                  </div>

                  {/* Link to Full Appointment Details */}
                  <Button 
                    variant="outline" 
                    className="w-full mt-2"
                    onClick={() => navigate(`/warehouse/trucks/${deliveryAppointment.truck_id}`)}
                  >
                    View Full Appointment Details →
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-gray-500 mb-4">No delivery scheduled</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleScheduleDelivery}
                  >
                    Schedule Delivery
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
